import { ChartCard, type ChartCardState } from "../../ChartCard";
import { UsageChart } from "../../UsageChart";
import type { TenantUsageHistory } from "../../../../hooks/useAdminStatus";

type EmbeddingSpendPanelProps = {
  readonly status: "loading" | "ready" | "error";
  readonly usage: TenantUsageHistory | null;
  readonly error: string | null;
};

const hasSpend = (usage: TenantUsageHistory | null): boolean =>
  usage != null && usage.points.some((p) => p.spentUsd != null);

const cardState = (
  status: EmbeddingSpendPanelProps["status"],
  usage: TenantUsageHistory | null,
): ChartCardState => {
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  if (!hasSpend(usage)) return "empty";
  return "ready";
};

// Four decimals, not two. A full re-index of one of the largest tenants costs
// under two cents, so the AI-spend panel's cent precision would render almost
// every real value as $0.00 and read as "no spend" when there is some.
const summary = (usage: TenantUsageHistory | null): string | undefined => {
  if (usage?.latestSpentUsd == null) return undefined;
  const limit = usage.limitUsd != null ? ` of $${usage.limitUsd.toFixed(2)}` : " spent";
  const period = usage.periodLabel != null ? ` (${usage.periodLabel})` : "";
  return `$${usage.latestSpentUsd.toFixed(4)}${limit}${period}`;
};

export const EmbeddingSpendPanel = ({
  status,
  usage,
  error,
}: EmbeddingSpendPanelProps): JSX.Element => (
  <ChartCard
    title="Embedding spend"
    subtitle={summary(usage)}
    state={cardState(status, usage)}
    emptyText="No embedding spend yet."
    hint="Memory-search embeddings. None usually means no embedding key, or keyword-only search."
    errorText={error ?? undefined}
  >
    {usage != null && <UsageChart usage={usage} />}
  </ChartCard>
);
